import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Loader2,
  Lock,
  LogOut,
  RefreshCw,
  Inbox,
  FileText,
  HelpCircle,
  Briefcase,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/layout/Logo";
import SubmissionsPanel from "@/components/admin/SubmissionsPanel";
import ContentPanel from "@/components/admin/ContentPanel";
import FaqPanel from "@/components/admin/FaqPanel";
import JobsPanel from "@/components/admin/JobsPanel";
import AccountPanel from "@/components/admin/AccountPanel";
import {
  adminLogin,
  adminMe,
  fetchSubmissions,
  setToken,
  getToken,
  clearToken,
} from "@/lib/apiClient";

const NAV = [
  { key: "submissions", label: "Submissions", icon: Inbox },
  { key: "content", label: "Page content", icon: FileText },
  { key: "faqs", label: "FAQs", icon: HelpCircle },
  { key: "jobs", label: "Jobs", icon: Briefcase },
  { key: "account", label: "Account", icon: UserCog },
];

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [booting, setBooting] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [checking, setChecking] = useState(false);
  const [section, setSection] = useState("submissions");
  const [subs, setSubs] = useState(null);
  const [loadingSubs, setLoadingSubs] = useState(false);

  const loadSubs = async () => {
    setLoadingSubs(true);
    try {
      setSubs(await fetchSubmissions());
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Failed to load submissions.");
    } finally {
      setLoadingSubs(false);
    }
  };

  useEffect(() => {
    if (getToken()) {
      adminMe()
        .then(() => {
          setAuthed(true);
          loadSubs();
        })
        .catch(() => clearToken())
        .finally(() => setBooting(false));
    } else {
      setBooting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setChecking(true);
    try {
      const res = await adminLogin(email.trim(), password);
      setToken(res.token);
      setAuthed(true);
      toast.success("Signed in.");
      loadSubs();
    } catch {
      toast.error("Invalid email or password.");
    } finally {
      setChecking(false);
    }
  };

  const logout = () => {
    clearToken();
    setAuthed(false);
    setSubs(null);
    setEmail("");
    setPassword("");
  };

  if (booting) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="dark flex min-h-screen items-center justify-center bg-background px-4">
        <div className="hero-glow pointer-events-none fixed inset-0" />
        <div className="bg-sentira-grid bg-radial-fade pointer-events-none fixed inset-0 opacity-50" />
        <div className="relative w-full max-w-sm rounded-[var(--radius-card)] border border-border bg-card p-8 shadow-[var(--shadow-card)]">
          <div className="mb-6 flex justify-center">
            <Logo />
          </div>
          <div className="mb-6 flex flex-col items-center text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-secondary">
              <Lock className="h-5 w-5 text-primary" />
            </div>
            <h1 className="mt-4 text-xl font-semibold">Admin sign in</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Sign in to manage content & submissions.
            </p>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@sentira.io"
                data-testid="admin-email-input"
                className="bg-[hsl(220_30%_8%)] border-border focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                data-testid="admin-password-input"
                className="bg-[hsl(220_30%_8%)] border-border focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
              />
            </div>
            <Button
              type="submit"
              disabled={checking}
              className="w-full bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]"
              data-testid="admin-login-button"
            >
              {checking ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>) : "Sign in"}
            </Button>
          </form>
          <Link to="/" className="mt-6 block text-center text-xs text-muted-foreground hover:text-foreground">
            ← Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-[rgba(11,15,20,0.92)] backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[var(--container-max)] items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Logo />
            <Badge variant="secondary" className="border-border bg-secondary text-muted-foreground">Admin</Badge>
          </div>
          <div className="flex items-center gap-2">
            {section === "submissions" && (
              <Button variant="secondary" size="sm" onClick={loadSubs} disabled={loadingSubs} data-testid="admin-refresh-button">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${loadingSubs ? "animate-spin" : ""}`} /> Refresh
              </Button>
            )}
            <Button asChild variant="ghost" size="sm">
              <Link to="/" data-testid="admin-view-site-link">View site</Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-button">
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[var(--container-max)] flex-col gap-8 px-4 py-10 sm:px-6 lg:flex-row lg:px-8">
        <aside className="lg:w-56 lg:flex-shrink-0">
          <nav className="flex gap-2 overflow-x-auto lg:flex-col lg:gap-1" data-testid="admin-nav">
            {NAV.map((n) => (
              <button
                key={n.key}
                onClick={() => setSection(n.key)}
                data-testid={`admin-nav-${n.key}`}
                className={`flex items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  section === n.key
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                }`}
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 flex-1">
          {section === "submissions" &&
            (loadingSubs && !subs ? (
              <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : (
              <SubmissionsPanel data={subs} />
            ))}
          {section === "content" && <ContentPanel />}
          {section === "faqs" && <FaqPanel />}
          {section === "jobs" && <JobsPanel />}
          {section === "account" && <AccountPanel />}
        </div>
      </div>
    </div>
  );
}
